import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'; // Ensure useMemo is imported
import { useAuth } from '../lib/auth/AuthContext';
import { useApi } from '../lib/apiClient';
import { useQuery, useQueryClient, QueryClient, QueryClientProvider } from '@tanstack/react-query'; // Add these
import {
    Input,
    Skeleton,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@pagespeedify/ui";
import { toast } from "sonner";
import { Check, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

// Import the shared Paddle type declarations from types/index.ts
import { PaddleEventData, ApiResponse } from '../types'; // Ensure ApiResponse is imported if not already

const MIN_CREDITS = 1000;
const CREDIT_INCREMENT = 100;
const COST_PER_1000_CREDITS = 10.00;
const CURRENCY = 'USD';

interface CreateCreditPurchaseData {
    success: boolean;
    message?: string;
    paddle_transaction_id?: string;
    transactionId?: string;
    updatedBalance?: number;
    details?: string;
}

interface PersistentMessage {
    type: 'success' | 'error' | 'info';
    text: string;
}

// --- Interfaces for Credit History (React Query version) ---
interface CreditTransaction {
    id: string;
    user_id: string;
    acting_user_id?: string;
    team_id?: string;
    type: 'purchase' | 'usage' | 'refund' | 'adjustment' | 'perk' | 'activation_perk' | 'bonus';
    amount: number;
    balance_after: number;
    description?: string;
    reference_id?: string;
    reference_type?: string;
    timestamp: string;
    promotion_id?: string;
    payment_method?: string;
    payment_transaction_id?: string;
    related_entity_id?: string | null;
    related_entity_type?: string | null;
    metadata?: Record<string, any> | null;
}

interface PaginationDetails {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

// REMOVE or ensure CreditTransactionHistoryResponseData is not causing confusion if ApiResponse is flat
// interface CreditTransactionHistoryResponseData {
//   transactions: CreditTransaction[];
//   pagination: PaginationDetails;
// }

// Define the actual flat structure for the credit history query result
interface CreditHistoryQueryResult {
    success: boolean;
    message?: string;
    transactions: CreditTransaction[];
    pagination: PaginationDetails;
}
// --- End of Credit History Interfaces ---

// Define a type for sorting configuration
type CreditSortConfig = {
    key: keyof CreditTransaction;
    direction: 'ascending' | 'descending';
} | null;

interface CreditHistoryDisplayProps {
    userId: string;
    enabled: boolean;
}
// ... (after interface definitions) ...

function CreditHistoryDisplay({ userId, enabled }: CreditHistoryDisplayProps) {
    const apiClient = useApi();
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState<CreditSortConfig>(null);
    const itemsPerPage = 10;

    const fetchCreditHistoryAPI = async (page: number, limit: number): Promise<CreditHistoryQueryResult> => {
        const response = await apiClient.get<CreditHistoryQueryResult>(`/credits/history?page=${page}&limit=${limit}`);
        if (!response.success) {
            throw new Error(response.message || 'Failed to fetch credit history data from API (API error)');
        }
        return response;
    };

    const {
        data: historyQueryData,
        isLoading, // True during initial fetch and if query is hard-refetched without data
        isError,
        error: queryError,
        isFetching, // True whenever any fetch is in progress (initial or background)
        refetch,
    } = useQuery<CreditHistoryQueryResult, Error>({
        queryKey: ['creditHistory', userId, currentPage, itemsPerPage],
        queryFn: () => fetchCreditHistoryAPI(currentPage, itemsPerPage),
        enabled: !!userId && enabled,
        staleTime: 5 * 60 * 1000,
        cacheTime: 15 * 60 * 1000,
        retry: 0,
    });

    const transactions = historyQueryData?.transactions;
    const pagination = historyQueryData?.pagination;

    const sortedTransactions = useMemo(() => {
        if (!transactions) return [];
        let sortableItems = [...transactions];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfig.key]; const valB = b[sortConfig.key];
                if (valA === null || valA === undefined) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valB === null || valB === undefined) return sortConfig.direction === 'ascending' ? 1 : -1;
                if (sortConfig.key === 'amount' || sortConfig.key === 'balance_after') return sortConfig.direction === 'ascending' ? (valA as number) - (valB as number) : (valB as number) - (valA as number);
                if (sortConfig.key === 'timestamp') { const dateA = new Date(valA as string).getTime(); const dateB = new Date(valB as string).getTime(); return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA; }
                const strA = String(valA).toLowerCase(); const strB = String(valB).toLowerCase();
                if (strA < strB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (strA > strB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        } else { sortableItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()); }
        return sortableItems;
    }, [transactions, sortConfig]);

    const requestSort = (key: keyof CreditTransaction) => { let direction: 'ascending' | 'descending' = 'ascending'; if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending'; setSortConfig({ key, direction }); };
    const getSortIndicator = (key: keyof CreditTransaction) => { if (!sortConfig || sortConfig.key !== key) return null; return sortConfig.direction === 'ascending' ? <ChevronUp className="h-4 w-4 inline ml-1" /> : <ChevronDown className="h-4 w-4 inline ml-1" />; };
    const handleNextPage = () => { if (pagination && currentPage < pagination.totalPages) setCurrentPage(prev => prev + 1); };
    const handlePreviousPage = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };
    const formatTransactionTypeLocal = (type: string): string => { switch (type) { case 'activation_perk': return 'Activation Perk'; case 'purchase': return 'Purchase'; case 'usage': return 'Usage'; case 'refund': return 'Refund'; case 'bonus': return 'Bonus'; case 'adjustment': return 'Adjustment'; default: return type.charAt(0).toUpperCase() + type.slice(1); } };
    const formatAmountLocal = (amount: number, type: string): string => { if (type === 'usage' && amount > 0) return `-${amount}`; return amount > 0 ? `+${amount}` : `${amount}`; };
    const getAmountColorLocal = (amount: number, type: string): string => { if (type === 'usage' || amount < 0) return 'text-red-600 dark:text-red-400'; return 'text-green-600 dark:text-green-400'; };


    // 1. Handle initial loading state
    if (isLoading) {
        return <CardContent><Skeleton className="h-8 w-full mb-2" /><Skeleton className="h-8 w-full mb-2" /><Skeleton className="h-8 w-full mb-2" /><Skeleton className="h-8 w-full" /></CardContent>;
    }

    // 2. Handle query error state (network issues, etc.)
    if (isError) {
        return <CardContent><div className="text-center py-8"><div className="text-red-600 dark:text-red-400 mb-2">Failed to load transaction history</div><div className="text-sm text-gray-600 dark:text-gray-400 mb-4">{queryError?.message || 'An unknown error occurred.'}</div><Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4 mr-2" />Retry</Button></div></CardContent>;
    }

    // 3. Handle API returning success:false (after data is loaded, isLoading is false)
    // historyQueryData should be defined here if isLoading and isError are false.
    if (historyQueryData && !historyQueryData.success) {
        return <CardContent><div className="text-center py-8"><div className="text-red-600 dark:text-red-400 mb-2">Failed to load transaction history</div><div className="text-sm text-gray-600 dark:text-gray-400 mb-4">{historyQueryData.message || 'API returned an error.'}</div><Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4 mr-2" />Retry</Button></div></CardContent>;
    }

    // 4. Handle no transactions found (data loaded, success is true, but transactions array is empty)
    if (!transactions || transactions.length === 0) {
        // This implies historyQueryData is loaded and historyQueryData.success was true (or not explicitly false and caught above)
        return <CardContent><div className="text-center py-8 text-gray-600 dark:text-gray-400">No credit transactions found.</div></CardContent>;
    }

    // 5. If none of the above, data is available and valid, render the table
    return (
        <CardContent>
            {isFetching && <p className="text-xs text-gray-500 dark:text-gray-400 my-2 animate-pulse">Updating history...</p>} {/* For background updates when table is already visible */}
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader><TableRow>
                        <TableHead onClick={() => requestSort('timestamp')} className="cursor-pointer whitespace-nowrap">Date {getSortIndicator('timestamp')}</TableHead>
                        <TableHead onClick={() => requestSort('type')} className="cursor-pointer whitespace-nowrap">Type {getSortIndicator('type')}</TableHead>
                        <TableHead onClick={() => requestSort('description')} className="cursor-pointer">Description {getSortIndicator('description')}</TableHead>
                        <TableHead onClick={() => requestSort('amount')} className="text-right cursor-pointer whitespace-nowrap">Amount {getSortIndicator('amount')}</TableHead>
                        <TableHead onClick={() => requestSort('balance_after')} className="text-right cursor-pointer whitespace-nowrap">Balance After {getSortIndicator('balance_after')}</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{sortedTransactions.map((transaction) => (<TableRow key={transaction.id}> <TableCell className="font-mono text-xs whitespace-nowrap">{new Date(transaction.timestamp).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</TableCell> <TableCell className="whitespace-nowrap"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">{formatTransactionTypeLocal(transaction.type)}</span></TableCell> <TableCell className="max-w-sm truncate hover:whitespace-normal">{transaction.description || 'N/A'}</TableCell> <TableCell className={`text-right font-medium whitespace-nowrap ${getAmountColorLocal(transaction.amount, transaction.type)}`}>{formatAmountLocal(transaction.amount, transaction.type)}</TableCell> <TableCell className="text-right font-mono whitespace-nowrap">{transaction.balance_after}</TableCell> </TableRow>))}</TableBody>
                </Table>
            </div>
            {pagination && pagination.totalPages > 1 && (<div className="flex items-center justify-between mt-4 pt-4 border-t"><div className="text-sm text-gray-600 dark:text-gray-400">Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} transactions</div><div className="flex items-center gap-2"><Button variant="outline" size="sm" onClick={handlePreviousPage} disabled={currentPage <= 1 || isFetching}>Previous</Button><span className="text-sm">Page {pagination.page} of {pagination.totalPages}</span><Button variant="outline" size="sm" onClick={handleNextPage} disabled={currentPage >= pagination.totalPages || isFetching}>Next</Button></div></div>)}
        </CardContent>
    );
}

// ... CreditsPageContent function starts here ...
function CreditsPageContent() {
    const { user, isLoading: authLoading, refreshUser } = useAuth();
    const api = useApi();
    const queryClient = useQueryClient(); // Add this line

    // States for credit purchase flow (these remain)
    const [userCredits, setUserCredits] = useState<number | string | null>(null);
    const [persistentMessage, setPersistentMessage] = useState<PersistentMessage | null>(null);
    const [creditAmountInput, setCreditAmountInput] = useState<string>(MIN_CREDITS.toString());
    const [calculatedPrice, setCalculatedPrice] = useState<number>(COST_PER_1000_CREDITS);
    const [amountError, setAmountError] = useState<string | null>(null);
    const [isValidAmount, setIsValidAmount] = useState<boolean>(true);

    // Processing and UI states
    const [isProcessingPurchase, setIsProcessingPurchase] = useState<boolean>(false);
    const [paddleStatus, setPaddleStatus] = useState<{
        isVisible: boolean;
        status: 'loading' | 'processing' | 'success' | 'error' | 'closed';
        message: string;
    }>({ isVisible: false, status: 'loading', message: '' });

    const [showSuccessBanner, setShowSuccessBanner] = useState<{
        isVisible: boolean;
        creditsAmount?: number;
        newBalance?: number;
    }>({ isVisible: false });

    // Pending checkout action
    const [pendingCheckoutAction, setPendingCheckoutAction] = useState<{
        actionType: 'creditPurchase' | null;
        transactionId: string | null;
        creditsAmount?: number;
    }>({
        actionType: null,
        transactionId: null,
        creditsAmount: undefined
    });

    // State for toggling history visibility (this remains)
    const [showTransactionHistory, setShowTransactionHistory] = useState<boolean>(false);

    // Refs for validation
    const creditAmountRef = useRef(MIN_CREDITS);
    const calculatedPriceRef = useRef(COST_PER_1000_CREDITS);

    // Effect to initialize user credits display
    useEffect(() => {
        if (!authLoading) {
            console.log('[Credits] User credits updated from auth context:', user?.credits);
            setUserCredits(user?.credits ?? 'N/A');
        } else {
            setUserCredits('Loading...');
        }
    }, [user?.credits, authLoading]); // Changed from [user, authLoading] to [user?.credits, authLoading]

    // Function to validate input and calculate price
    const validateAndCalculate = useCallback((amountStr: string) => {
        const amount = parseInt(amountStr, 10);
        let error: string | null = null;
        let price = 0;
        let valid = false;

        if (isNaN(amount)) {
            error = "Please enter a valid number.";
        } else if (amount < MIN_CREDITS) {
            error = `Minimum purchase is ${MIN_CREDITS} credits.`;
        } else if (amount % CREDIT_INCREMENT !== 0) {
            error = `Credits must be in increments of ${CREDIT_INCREMENT}.`;
        } else {
            price = (amount / 1000) * COST_PER_1000_CREDITS;
            valid = true;
        }

        setAmountError(error);
        setCalculatedPrice(price);
        setIsValidAmount(valid);

        // Update refs
        creditAmountRef.current = valid ? amount : 0;
        calculatedPriceRef.current = valid ? price : 0;
    }, []);

    // Effect to re-validate whenever input changes
    useEffect(() => {
        validateAndCalculate(creditAmountInput);
    }, [creditAmountInput, validateAndCalculate]);

    // Handle changes to the credit amount input
    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCreditAmountInput(event.target.value);
        setPersistentMessage(null);
    };

    // Main purchase handler with enhanced Paddle integration
    const handlePurchaseCredits = async () => {
        const currentAmount = creditAmountRef.current;
        const currentPrice = calculatedPriceRef.current;

        if (!isValidAmount || currentAmount <= 0 || currentPrice <= 0) {
            toast.error('Please enter a valid credit amount before purchasing.');
            return;
        }

        console.log(`[Credits] Starting credit purchase for ${currentAmount} credits, $${currentPrice.toFixed(2)}`);

        setIsProcessingPurchase(true);
        setPersistentMessage(null);
        setPaddleStatus({
            isVisible: true,
            status: 'processing',
            message: `Creating credit purchase for ${currentAmount} credits...`
        });

        try {
            const requestBody = {
                amount: currentAmount,
                type: 'purchase',
                description: `Credit Purchase - ${currentAmount} credits`,
            };

            const response = await api.post<{
                success: boolean;
                paddle_transaction_id?: string;
                transactionId?: string;
                amount?: number;
                message?: string;
                data?: {
                    paddle_transaction_id?: string;
                    transactionId?: string;
                };
            }>('/credits', requestBody);

            console.log('[Credits] Credit purchase response:', response);

            const transactionId = response?.data?.paddle_transaction_id ||
                response?.data?.transactionId ||
                response?.paddle_transaction_id ||
                response?.transactionId;

            if (response?.success && transactionId && window.Paddle && typeof window.Paddle.Checkout.open === 'function') {

                toast.info(`Opening checkout for ${currentAmount} credits...`);
                setPaddleStatus({
                    isVisible: true,
                    status: 'processing',
                    message: `Opening checkout for ${currentAmount} credits...`
                });

                setPendingCheckoutAction({
                    actionType: 'creditPurchase',
                    transactionId: transactionId,
                    creditsAmount: currentAmount
                });

                window.Paddle.Checkout.open({
                    transactionId: transactionId,
                    eventCallback: function (eventData: PaddleEventData) {
                        console.log('[Credits] Paddle eventCallback:', eventData.name, eventData);

                        if (eventData.name === 'checkout.loaded') {
                            console.log('[Credits] Checkout loaded for credit purchase');
                            setPaddleStatus({
                                isVisible: true,
                                status: 'processing',
                                message: `Complete payment for ${currentAmount} credits...`
                            });
                        } else if (eventData.name === 'checkout.closed') {
                            console.log('[Credits] Checkout closed by user');
                            setPaddleStatus({
                                isVisible: true,
                                status: 'closed',
                                message: 'Checkout was closed. You can try again.'
                            });

                            setTimeout(() => {
                                setIsProcessingPurchase(false);
                                setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
                                setPendingCheckoutAction({ actionType: null, transactionId: null, creditsAmount: undefined });
                            }, 3000);
                        }
                    }
                });

            } else if (response?.success && response.message && (response.message.includes("No payment required") || response.message.includes("successfully created"))) {
                await handlePurchaseSuccess(currentAmount);
            } else {
                const errorMsg = !response?.success ? (response?.message || 'Failed to create credit purchase.') :
                    !transactionId ? 'Could not initiate payment. Transaction details missing.' :
                        !window.Paddle ? 'Payment provider (Paddle) is not available.' :
                            'An unexpected error occurred before opening checkout.';

                toast.error(errorMsg);
                setPersistentMessage({ type: 'error', text: errorMsg });
                setIsProcessingPurchase(false);
                setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
            }

        } catch (error: any) {
            console.error('[Credits] Error during credit purchase:', error);
            const errorMessage = error.response?.data?.message || error.message || 'An error occurred during credit purchase.';
            toast.error(errorMessage);
            setPersistentMessage({ type: 'error', text: errorMessage });
            setIsProcessingPurchase(false);
            setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
        }
    };

    // Handle successful purchase (called after payment completion)
    const handlePurchaseSuccess = useCallback(async (creditsAmount: number) => {
        console.log(`[Credits] Processing successful purchase of ${creditsAmount} credits (handlePurchaseSuccess)`);

        try {
            // Immediate refresh attempt (may not have updated credits yet if webhook is still processing)
            await refreshUser();
            console.log('[Credits] Immediate refreshUser() called. Credits may not be updated yet if webhook is processing.');

            // Schedule delayed refreshes to catch webhook updates
            // The Paddle webhook needs time to process and update the database
            setTimeout(async () => {
                console.log('[Credits] Delayed refreshUser() - 2 seconds after purchase');
                await refreshUser();
            }, 2000);

            setTimeout(async () => {
                console.log('[Credits] Delayed refreshUser() - 4 seconds after purchase (final check)');
                await refreshUser();
            }, 4000);

            // Modify toast and persistent message to not show immediate new balance
            toast.success(`Successfully purchased ${creditsAmount} credits!`, {
                description: `Your credit balance will update shortly.`,
                duration: 6000
            });
            setPersistentMessage({
                type: 'success',
                text: `Successfully purchased ${creditsAmount} credits!`
            });
            setShowSuccessBanner({
                isVisible: true,
                creditsAmount
                // newBalance is removed as we don't have it reliably here
            });

            setCreditAmountInput(MIN_CREDITS.toString());
            validateAndCalculate(MIN_CREDITS.toString());

            if (showTransactionHistory && user?.uid) {
                console.log('[Credits] Invalidating credit history query for user:', user.uid);
                // Ensure queryClient.invalidateQueries is called correctly
                await queryClient.invalidateQueries({ queryKey: ['creditHistory', user.uid], exact: false });
            } else {
                console.log('[Credits] Credit history not visible or user UID missing, not invalidating immediately. showTransactionHistory:', showTransactionHistory, 'UID available:', !!user?.uid);
            }
        } catch (error) {
            console.error('[Credits] Error during post-purchase success handling:', error);
            toast.error('Purchase completed, but an error occurred updating the page. Please refresh.');
        }
    }, [refreshUser, validateAndCalculate, showTransactionHistory, queryClient, user?.uid]); // Keep user.uid if needed for query invalidation key

    // Custom event listener for Paddle checkout completion
    useEffect(() => {
        const handleCustomCheckoutCompleted = (event: Event) => {
            const customEvent = event as CustomEvent<{ transactionId: string; paddleEventData: any }>;
            if (!customEvent.detail) {
                console.warn('[Credits] Received "paddleCheckoutActionCompleted" but event.detail was null.');
                return;
            }

            const { transactionId: completedTransactionId, paddleEventData } = customEvent.detail;
            console.log('[Credits] Received "paddleCheckoutActionCompleted". Pending action:', JSON.stringify(pendingCheckoutAction), 'Event transaction_id:', completedTransactionId);

            if (pendingCheckoutAction.actionType === 'creditPurchase' &&
                pendingCheckoutAction.transactionId &&
                pendingCheckoutAction.transactionId === completedTransactionId) {

                console.log(`[Credits] Matched 'creditPurchase' action for transaction ${completedTransactionId}`);

                const creditsAmountFromAction = pendingCheckoutAction.creditsAmount || 0; // Use a different variable name

                setPaddleStatus({
                    isVisible: true,
                    status: 'success',
                    message: `Payment completed for ${creditsAmountFromAction} credits!`
                });

                handlePurchaseSuccess(creditsAmountFromAction) // Pass the creditsAmount
                    .then(() => {
                        console.log('[Credits] handlePurchaseSuccess completed');
                    })
                    .catch(err => {
                        console.error('[Credits] Error in handlePurchaseSuccess:', err);
                        toast.error('Purchase completed, but an error occurred updating the page. Please refresh.');
                    })
                    .finally(() => {
                        setIsProcessingPurchase(false);
                        setPendingCheckoutAction({ actionType: null, transactionId: null, creditsAmount: undefined });
                        setTimeout(() => setPaddleStatus({ isVisible: false, status: 'loading', message: '' }), 3000);
                    });

            } else {
                if (pendingCheckoutAction.transactionId && pendingCheckoutAction.actionType === 'creditPurchase') {
                    console.log(`[Credits] Received "paddleCheckoutActionCompleted", but transaction ID did not match. Expected: ${pendingCheckoutAction.transactionId}, Got: ${completedTransactionId}`);
                }
            }
        };

        window.addEventListener('paddleCheckoutActionCompleted', handleCustomCheckoutCompleted);
        return () => {
            window.removeEventListener('paddleCheckoutActionCompleted', handleCustomCheckoutCompleted);
        };
    }, [pendingCheckoutAction, handlePurchaseSuccess]);


    // Effect to initialize user credits display (this updates the main balance on the page)
    useEffect(() => {
        if (!authLoading) {
            console.log('[Credits] User credits updated from auth context:', user?.credits);
            setUserCredits(user?.credits ?? 'N/A');
        } else {
            setUserCredits('Loading...');
        }
    }, [user?.credits, authLoading]);

    // Loading state while checking auth
    if (authLoading) {
        return (
            <div className="container mx-auto p-4">
                <Skeleton className="h-8 w-1/4 mb-4" />
                <Skeleton className="h-20 w-full max-w-md mb-6" />
                <Skeleton className="h-32 w-full max-w-md" />
            </div>
        );
    }

    // Check for Paddle configuration
    const paddleClientKey = import.meta.env.VITE_PADDLE_CLIENT_KEY;
    if (!paddleClientKey) {
        return (
            <div className="container mx-auto p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <strong className="font-bold">Configuration Error: </strong>
                    <span className="block sm:inline">Paddle Client Key is not configured. Payment cannot proceed.</span>
                </div>
            </div>
        );
    }

    // UI Components
    const PaddleStatusDisplay: React.FC = () => {
        if (!paddleStatus.isVisible) return null;

        return (
            <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${paddleStatus.status === 'success' ? 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:text-green-200 dark:border-green-700' :
                    paddleStatus.status === 'error' ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700' :
                        paddleStatus.status === 'closed' ? 'bg-yellow-100 border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-700' :
                            'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-700'
                } border`}>
                <div className="flex items-center gap-2">
                    {paddleStatus.status === 'processing' && <Loader2 className="h-4 w-4 animate-spin" />}
                    {paddleStatus.status === 'success' && <Check className="h-4 w-4" />}
                    <span className="text-sm font-medium">{paddleStatus.message}</span>
                </div>
            </div>
        );
    };

    const SuccessBanner: React.FC = () => {
        if (!showSuccessBanner.isVisible) return null;

        return (
            <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:text-green-200 dark:border-green-700 border p-4 rounded-lg shadow-lg max-w-md">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Check className="h-5 w-5" />
                        <span className="font-medium">
                            {showSuccessBanner.creditsAmount ?
                                `Successfully purchased ${showSuccessBanner.creditsAmount} credits!` :
                                'Purchase successful!'
                            }
                        </span>
                    </div>
                    <button
                        onClick={() => setShowSuccessBanner({ isVisible: false })}
                        className="text-green-600 hover:text-green-800"
                    >
                        ×
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            {/* Status Components */}
            <PaddleStatusDisplay />
            <SuccessBanner />

            <h1 className="text-2xl font-bold mb-4">Buy Credits</h1>

            {/* Display current balance and persistent message */}
            <p className="mb-2">
                Your current credit balance: <strong>{userCredits}</strong>
            </p>

            {persistentMessage && (
                <div
                    className={`mb-4 px-4 py-3 rounded relative ${persistentMessage.type === 'success'
                            ? 'bg-green-100 border border-green-400 text-green-700'
                            : persistentMessage.type === 'info'
                                ? 'bg-blue-100 border border-blue-400 text-blue-700'
                                : 'bg-red-100 border border-red-400 text-red-700'
                        }`}
                    role="alert"
                >
                    {persistentMessage.text}
                </div>
            )}

            {/* Credit Amount Input Section */}
            <div className="mb-6 border p-4 rounded max-w-md">
                <label htmlFor="creditAmount" className="block text-lg font-semibold mb-2">
                    Amount of Credits to Purchase
                </label>
                <Input
                    type="number"
                    id="creditAmount"
                    name="creditAmount"
                    value={creditAmountInput}
                    onChange={handleAmountChange}
                    min={MIN_CREDITS}
                    step={CREDIT_INCREMENT}
                    className={`w-full ${amountError ? 'border-red-500' : ''}`}
                    disabled={isProcessingPurchase}
                    placeholder={`Minimum ${MIN_CREDITS}`}
                    aria-invalid={!!amountError}
                    aria-describedby={amountError ? "amount-error-message" : undefined}
                />
                {amountError && (
                    <p id="amount-error-message" className="text-red-600 text-sm mt-1">{amountError}</p>
                )}
                {!amountError && isValidAmount && (
                    <p className="text-lg font-semibold mt-2">
                        Cost: ${calculatedPrice.toFixed(2)} {CURRENCY}
                    </p>
                )}
                <p className="text-sm text-gray-600 mt-1">
                    Minimum {MIN_CREDITS} credits. Must be in increments of {CREDIT_INCREMENT}. Cost is ${COST_PER_1000_CREDITS.toFixed(2)} per 1000 credits.
                </p>
            </div>

            {/* Purchase Button Section */}
            {isValidAmount && !amountError && (
                <Card className="mt-6 max-w-md">
                    <CardHeader>
                        <CardTitle>Complete Purchase</CardTitle>
                        {!isProcessingPurchase && (
                            <CardDescription>
                                You are buying {creditAmountRef.current} credits for ${calculatedPriceRef.current.toFixed(2)} {CURRENCY}.
                            </CardDescription>
                        )}
                    </CardHeader>
                    <CardContent>
                        {isProcessingPurchase ? (
                            <div className="text-center py-4">
                                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                                <p>Processing your purchase... Please wait.</p>
                            </div>
                        ) : (
                            <Button
                                onClick={handlePurchaseCredits}
                                size="lg"
                                className="w-full"
                                disabled={!isValidAmount || isProcessingPurchase}
                            >
                                Purchase {creditAmountRef.current} Credits for ${calculatedPriceRef.current.toFixed(2)}
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Credit Transaction History Section using the new React Query component */}
            <Card className="mt-8">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle>Credit Transaction History</CardTitle>
                            <CardDescription>View all your credit transactions including purchases, usage, and perks.</CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setShowTransactionHistory(!showTransactionHistory)} aria-expanded={showTransactionHistory} aria-controls="credit-history-content">
                            {showTransactionHistory ? (<><ChevronUp className="h-4 w-4 mr-2" />Hide History</>) : (<><ChevronDown className="h-4 w-4 mr-2" />Show History</>)}
                        </Button>
                    </div>
                </CardHeader>
                <div id="credit-history-content" className={showTransactionHistory ? '' : 'hidden'}>
                    {showTransactionHistory && user?.uid && !authLoading && (
                        <CreditHistoryDisplay userId={user.uid} enabled={showTransactionHistory} />
                    )}
                    {showTransactionHistory && authLoading && (
                        <CardContent><Skeleton className="h-20 w-full" /></CardContent>
                    )}
                </div>
            </Card>
        </div>
    );
}

const queryClient = new QueryClient(); // Create a new client instance

export default function CreditsPage() {
    return (
        <QueryClientProvider client={queryClient}>
            <CreditsPageContent />
        </QueryClientProvider>
    );
}